import {
  Button,
  Heading,
  InfoListItem,
  ListContainer,
  Typography,
} from '@altify/components';
import { useContent } from '@altify/utils';
import { sharedContent } from 'app/_authenticated/recurring-buys/recurringBuyShared.content';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import { RecurringBuyInterval } from 'app/_authenticated/trade/recurring-buy/models/RecurringBuyInterval';
import { RecurringBuySchedule } from 'app/_authenticated/trade/recurring-buy/models/RecurringBuySchedule';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import { useFunctionalCurrency, useProduct } from 'hooks/products/useProducts';
import { RecurringBuyResponseDayOfWeekEnum } from 'services/generated';
import { useGetRecurringBuyByIdQuery } from 'store/slices/recurringBuy-api';
import { lowerCase } from 'utils/casing';
import { formatCurrency } from 'utils/currencies';
import { content } from './RecurringBuyDetailsModal.content';
import { RecurringBuyDetailsSkeleton } from './RecurringBuyDetailsSkeleton';

interface RecurringBuyDetails {
  id: string;
  productCode: string;
  productName: string;
  paused: boolean;
  amount: string;
  totalInvestedAmount: number;
  interval: RecurringBuyInterval | undefined;
  createdAt: Date;
  nextExecution: Date;
  successfulExecutions: number;
  dayOfWeek: RecurringBuyResponseDayOfWeekEnum | null | undefined;
  dayOfMonth: number | null | undefined;
}

const RecurringBuyDetailsModal = ({
  recurringBuyId,
  isOpen,
  onCancelRecurringBuy,
  onPauseRecurringBuy,
  onResumeRecurringBuy,
  cancelIsLoading,
  manageIsLoading,
  onClose,
}: {
  recurringBuyId: string;
  isOpen: boolean;
  onCancelRecurringBuy: () => void;
  onPauseRecurringBuy: () => void;
  onResumeRecurringBuy: (
    amount: number,
    productCode: string,
    schedule: string,
    nextExecution: string,
  ) => void;
  cancelIsLoading: boolean;
  manageIsLoading: boolean;
  onClose: () => void;
}) => {
  const localizedContent = useContent(content, sharedContent);
  const { functionalCurrency } = useFunctionalCurrency();

  const { data, isLoading } = useGetRecurringBuyByIdQuery(recurringBuyId);
  const { product: product } = useProduct(data?.productCode);

  const recurringBuy = {
    productCode: data?.productCode,
    productName: product?.name,
    paused: data?.paused ?? true,
    amount: formatCurrency(data?.amount, functionalCurrency?.code!),
    interval: lowerCase(data?.interval),
    createdAt: new Date(data?.createdAt!),
    nextExecution: new Date(data?.nextExecutionDate!),
    successfulExecutions: data?.successfulExecutions ?? 0,
    dayOfWeek: data?.dayOfWeek,
    dayOfMonth: data?.dayOfMonth,
  };

  const schedule = localizedContent.intervalReadable({
    interval: lowerCase(data?.interval),
    dayOfWeek: data?.dayOfWeek ?? undefined,
    dayOfMonth: data?.dayOfMonth ?? undefined,
    buyNow: false,
  } as RecurringBuySchedule);
  const nextExecution = localizedContent.nextExecutionDate(
    recurringBuy.nextExecution,
  );
  const list = localizedContent.panels(
    recurringBuy.paused,
    recurringBuy.amount,
    recurringBuy.interval,
    recurringBuy.createdAt,
    recurringBuy.nextExecution,
    recurringBuy.successfulExecutions,
    recurringBuy.dayOfWeek ?? undefined,
    recurringBuy.dayOfMonth,
  );
  return (
    <DialogOrBottomSheet
      isOpen={isOpen}
      blurEnabled={true}
      onClose={onClose}
      footerContent={
        <div className="w-full items-center px-4 pb-4">
          <Button
            disabled={isLoading}
            loading={cancelIsLoading}
            size="large"
            color={'secondary'}
            onPress={() => {
              onCancelRecurringBuy?.();
            }}
            text={localizedContent.cancelRecurringBuy}
            className="mt-2 w-full"
          />
          <Button
            disabled={isLoading}
            loading={manageIsLoading}
            size="large"
            color={'primary'}
            onPress={async () => {
              if (recurringBuy.paused) {
                onResumeRecurringBuy?.(
                  data?.amount ?? 0,
                  data?.productCode ?? '',
                  schedule,
                  nextExecution,
                );
              } else {
                onPauseRecurringBuy?.();
              }
            }}
            text={
              recurringBuy.paused
                ? localizedContent.resumeRecurringBuy
                : localizedContent.pauseRecurringBuy
            }
            className="mt-2"
          />
        </div>
      }
      backgroundColor="#0C1C2C">
      <div className="flex flex-col px-4 pb-4 w-full items-center">
        {isLoading ? (
          <RecurringBuyDetailsSkeleton />
        ) : (
          <>
            <CurrencyIcon
              code={recurringBuy.productCode}
              cornerIconBackground="#0C1C2C"
              bottomRightIcon="RECURRING_BUY"
              isLockedAsset={recurringBuy.paused}
              size={40}
            />
            <Heading level={2} className="text-center">
              {recurringBuy.productName}
            </Heading>
            <Typography
              size="large"
              className="mb-5 text-center text-[#A4A7C1]">
              {localizedContent.recurringBuy}
            </Typography>
            <ListContainer>
              {list.map(i => (
                <InfoListItem {...i} key={i.id} />
              ))}
            </ListContainer>
          </>
        )}
      </div>
    </DialogOrBottomSheet>
  );
};

export default RecurringBuyDetailsModal;
